import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface AdminCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  currentMonth: Date;
  onMonthChange: (direction: 'prev' | 'next') => void;
}

/**
 * Componente de calendário para administradores
 * Permite navegação mensal e seleção de datas sem restrições de negócio
 */
const AdminCalendar: React.FC<AdminCalendarProps> = ({
  selectedDate,
  onDateSelect,
  currentMonth,
  onMonthChange
}) => {
  
  /**
   * Formatar data para string YYYY-MM-DD
   */
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * Obter nome do mês formatado
   */
  const getMonthName = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  /**
   * Gerar os dias de um mês para exibição no calendário
   */
  const generateDaysInMonth = (monthDate: Date) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    
    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);
    
    // Primeiro dia da semana (0 = domingo, 1 = segunda, etc.)
    const startDayOfWeek = firstDay.getDay();
    
    // Dias do mês anterior para completar a primeira semana
    const prevMonthDays = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const prevDay = new Date(year, month, -i);
      prevMonthDays.push({
        date: prevDay,
        isCurrentMonth: false,
        isPrevMonth: true
      });
    }
    
    // Dias do mês atual
    const currentMonthDays = [];
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const currentDay = new Date(year, month, day);
      currentMonthDays.push({
        date: currentDay,
        isCurrentMonth: true,
        isPrevMonth: false
      });
    }
    
    // Dias do próximo mês para completar as semanas
    const totalCells = 42; // 6 semanas × 7 dias
    const remainingCells = totalCells - (prevMonthDays.length + currentMonthDays.length);
    const nextMonthDays = [];
    for (let day = 1; day <= remainingCells; day++) {
      const nextDay = new Date(year, month + 1, day);
      nextMonthDays.push({
        date: nextDay,
        isCurrentMonth: false,
        isPrevMonth: false
      });
    }
    
    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  /**
   * Obter classes CSS para cada dia do calendário
   */
  const getDayClasses = (dayInfo: { date: Date; isCurrentMonth: boolean }) => {
    const { date, isCurrentMonth } = dayInfo;
    const dateString = formatDateString(date);
    const isSelected = dateString === selectedDate;
    const isToday = dateString === formatDateString(new Date());
    
    let classes = 'w-10 h-10 flex items-center justify-center text-sm rounded-full cursor-pointer transition-all hover:bg-blue-50 ';
    
    if (!isCurrentMonth) {
      classes += 'text-gray-300 ';
    } else if (isSelected) {
      classes += 'bg-blue-600 text-white font-semibold ';
    } else if (isToday) {
      classes += 'bg-blue-100 text-blue-600 font-semibold ';
    } else {
      classes += 'text-gray-700 hover:bg-blue-100 ';
    }
    
    return classes;
  };

  const days = generateDaysInMonth(currentMonth);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header com navegação */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onMonthChange('prev')}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          title="Mês anterior"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        
        <h3 className="text-lg font-semibold text-gray-800 capitalize">
          {getMonthName(currentMonth)}
        </h3>
        
        <button
          onClick={() => onMonthChange('next')}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          title="Próximo mês"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Header dos dias da semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dayName) => (
          <div key={dayName} className="h-10 flex items-center justify-center text-xs font-medium text-gray-500">
            {dayName}
          </div>
        ))}
      </div>
      
      {/* Grid dos dias */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((dayInfo, dayIndex) => {
          const dateString = formatDateString(dayInfo.date);
          
          return (
            <div
              key={dayIndex}
              className={getDayClasses(dayInfo)}
              onClick={() => {
                if (dayInfo.isCurrentMonth) {
                  onDateSelect(dateString);
                }
              }}
              title={`Selecionar ${dayInfo.date.toLocaleDateString('pt-BR')}`}
            >
              {dayInfo.date.getDate()}
            </div>
          );
        })}
      </div>
      
      {/* Legenda */}
      <div className="mt-4 text-xs text-gray-500 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
          <span>Data selecionada</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-100 rounded-full"></div>
          <span>Hoje</span>
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;